/**
 * Treatment comparison data for /compare-treatments.
 * Every figure is sourced from the cited trials listed on the page.
 * Used by both the static comparison table and the interactive tool
 * so the numbers never diverge.
 */

export interface TreatmentData {
  id: string;
  name: string;
  fdaStatus: string;
  timeCommitment: string;
  timeToResponse: string;
  responseRemission: string;
  sideEffects: string;
  costCoverage: string;
  /** filter flags */
  fastest: boolean;
  leastTime: boolean;
  noAnesthesia: boolean;
}

export const TREATMENTS: TreatmentData[] = [
  {
    id: "saint",
    name: "SAINT®",
    fdaStatus: "FDA-cleared (510(k)) for MDD in adults",
    timeCommitment: "5 consecutive days (plus a mapping day)",
    timeToResponse: "Days — average 2.6 days to remission",
    responseRemission:
      "79% reached remission (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary.",
    sideEffects: "Headache, fatigue, scalp discomfort; small seizure risk",
    costCoverage:
      "Starting at $25,000; may be covered (coverage varies by plan); free benefits verification",
    fastest: true,
    leastTime: true,
    noAnesthesia: true,
  },
  {
    id: "standard-tms",
    name: "Standard TMS",
    fdaStatus: "FDA-cleared for MDD",
    timeCommitment: "6–9 weeks, 5 sessions/week (30–36 visits)",
    timeToResponse: "Weeks — typically 3–5 weeks to notice change",
    responseRemission:
      "Remission 17–32% (Janicak et al., J Clin Psychiatry 2010). Individual results may vary.",
    sideEffects: "Scalp discomfort, headache; small seizure risk",
    costCoverage: "Often covered by commercial plans and Medicare",
    fastest: false,
    leastTime: false,
    noAnesthesia: true,
  },
  {
    id: "deep-tms",
    name: "Deep TMS (H-coil)",
    fdaStatus: "FDA-cleared for MDD (BrainsWay H-coil)",
    timeCommitment: "6 weeks, 5 sessions/week",
    timeToResponse: "Weeks — typically 4–5 weeks",
    responseRemission:
      "Remission ~32% (Levkovitz et al., World Psychiatry 2015). Individual results may vary.",
    sideEffects: "Scalp discomfort, headache; small seizure risk",
    costCoverage: "Often covered by commercial plans and Medicare",
    fastest: false,
    leastTime: false,
    noAnesthesia: true,
  },
  {
    id: "spravato",
    name: "SPRAVATO® (esketamine)",
    fdaStatus: "FDA-approved for TRD and MDD with suicidal ideation",
    timeCommitment: "Induction: 2×/week for 4 weeks, then maintenance",
    timeToResponse: "Days to weeks — some response within 24 hours",
    responseRemission:
      "Remission ~27% at 4 weeks (Daly et al., Mol Psychiatry 2019). Individual results may vary.",
    sideEffects: "Dissociation, sedation, dizziness, nausea (REMS monitored)",
    costCoverage: "Covered by many plans including Medicare",
    fastest: false,
    leastTime: false,
    noAnesthesia: false,
  },
  {
    id: "ketamine",
    name: "IV Ketamine (off-label)",
    fdaStatus: "Off-label use of an FDA-approved drug",
    timeCommitment: "6 infusions over 2–3 weeks, then boosters",
    timeToResponse: "Hours to days — often within 24 hours",
    responseRemission:
      "Response ~71% (Ivanova et al., J Affect Disord 2019). Individual results may vary.",
    sideEffects: "Dissociation, elevated blood pressure, nausea",
    costCoverage: "Generally not covered (off-label); self-pay",
    fastest: true,
    leastTime: false,
    noAnesthesia: false,
  },
  {
    id: "ect",
    name: "ECT",
    fdaStatus: "Established treatment; not a device clearance",
    timeCommitment: "2–3 sessions/week for 3–6 weeks",
    timeToResponse: "Weeks — typically 2–4 weeks",
    responseRemission:
      "Remission ~48% (UK ECT Review Group, Lancet 2003). Individual results may vary.",
    sideEffects: "Memory problems, requires general anesthesia",
    costCoverage: "Covered by most plans including Medicare",
    fastest: false,
    leastTime: false,
    noAnesthesia: false,
  },
];

export const FILTERS: {
  key: "fastest" | "leastTime" | "noAnesthesia";
  label: string;
}[] = [
  { key: "fastest", label: "Fastest" },
  { key: "leastTime", label: "Least time in clinic" },
  { key: "noAnesthesia", label: "No anesthesia" },
];
